<template>
    <vx-expandable-card>
        <template v-slot:header> When </template>

        <template v-slot:content>
            <div v-if="initialCalendarLoading" class="flex justify-center items-center py-12">
                <vx-icon-loading class="animate-loading-spin text-2xl text-slate-500"></vx-icon-loading>
            </div>

            <div v-else-if="!updateDate && originalDate">
                <span class="text-xs font-normal text-slate-700 sm:text-base sm:font-semibold mr-1">
                    {{ dateFormatted }}
                </span>
                <vx-popover hover arrow placement="top">
                    <span
                        class="text-xs font-normal text-slate-700 underline decoration-dotted decoration-skip-ink-none decoration-thickness-[13.5%] underline-offset-2 underline-position-from-font sm:text-base sm:font-semibold"
                        data-test="when-date"
                    >
                        {{ tzFormatted }}
                    </span>

                    <template v-if="currentGroup?.group_id" v-slot:content> Uses the group's time zone. </template>
                </vx-popover>

                <vx-button @click="adjust" class="mt-4 lg:mt-6" size="lg" block data-test="adjust-date">
                    Adjust Date
                    <font-awesome-icon widthAuto class="text-base" :icon="faFilePen"></font-awesome-icon>
                </vx-button>
            </div>

            <template v-else>
                <template v-if="currentGroup?.group_id">
                    <div class="text-sm text-slate-800 font-extrabold">Start on</div>
                    <div class="text-sm text-slate-500" :class="{ 'mb-2': canScheduleNow }">
                        <span> Times shown in groups's time zone ({{ tzFormatted }}). </span>
                    </div>
                </template>

                <vx-tabs v-if="canScheduleNow" v-model="currentTab" aria-label="When">
                    <vx-tab value="now">Now</vx-tab>
                    <vx-tab value="later">Later</vx-tab>
                </vx-tabs>

                <template v-if="canScheduleNow && currentTab === 'now'">
                    <div class="text-center flex justify-center text-slate-600 text-lg font-normal my-12">
                        <span class="text-center block">
                            Your message will send immediately after clicking the launch button.
                        </span>
                    </div>
                </template>

                <quick-blast-rules-datepicker
                    v-if="!canScheduleNow || currentTab === 'later'"
                    v-model="date"
                    :campaignType="props.campaignType"
                    isRequired
                    :class="canScheduleNow ? 'my-4 lg:my-10' : 'my-4'"
                />

                <vx-button
                    v-if="date !== originalDate && originalDate && currentTab !== 'now'"
                    class="mb-4"
                    color="info"
                    size="lg"
                    block
                    @click="undoUpdateDate"
                    data-test="undo-date-change"
                >
                    Undo Date Change
                    <font-awesome-icon :icon="faRotateLeft" />
                </vx-button>
            </template>

            <vx-alert v-if="!initialCalendarLoading && (updateDate || (canScheduleNow && !originalDate))" color="info">
                You should respect safe sending hours. Texting is allowed between 8 am to 9 pm in the contact's time
                zone. We recommend scheduling between 10 am to 6 pm to drive the most engagement.
            </vx-alert>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import {
    VxExpandableCard,
    VxTabs,
    VxTab,
    VxAlert,
    VxPopover,
    VxButton,
    VxSwal,
    VxIconLoading,
} from '@voxie/frontend-components';
import { ref, watch, onMounted, toRef } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useQuickBlastWhen } from '~/components/quick-blast/useQuickBlastWhen';
import { useSendingCalendar } from '~/components/quick-blast/useSendingCalendar';
import { faFilePen, faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import QuickBlastRulesDatepicker from '~/components/quick-blast/QuickBlastRulesDatepicker.vue';

const props = defineProps({
    modelValue: {
        required: false,
    },
    campaignType: {
        type: String,
    },
});

const { currentGroup } = useCurrentGroup();
const { findClosestAvailableTime } = useSendingCalendar();

const currentTab = ref('later');
const updateDate = ref(false);
const originalDate = ref(props.modelValue);

const emit = defineEmits(['update:modelValue']);

const {
    date,
    canScheduleNow,
    initialCalendarLoading,
    dateFormatted,
    tzFormatted,
    campaignTypeRestricted,
    loadCurrentMonthCalendar,
    roundDateToNearestFiveMinutes,
} = useQuickBlastWhen(
    {
        campaignType: toRef(props, 'campaignType'),
        modelValue: toRef(props, 'modelValue'),
        currentTab,
    },
    emit
);

onMounted(async () => {
    if (!props.modelValue) {
        await loadCurrentMonthCalendar();
    }
});

watch(
    () => props.campaignType,
    () => {
        if (campaignTypeRestricted.value) {
            undoUpdateDate();
        }
    }
);

watch(currentTab, (newCurrentTab, oldCurrentTab) => {
    if (newCurrentTab === 'now') {
        date.value = 'now';
    } else if (newCurrentTab === 'later') {
        if (oldCurrentTab === 'now' && canScheduleNow.value) {
            date.value = findClosestAvailableTime();
        } else {
            date.value = undefined;
        }
    }
});

const adjust = async () => {
    const confirmed = await VxSwal.fire({
        title: 'Are you sure?',
        html: 'Modifying the date will re-run validation. Restrictions may apply that cause you to lose your currently scheduled date.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
    });

    if (confirmed.value) {
        updateDate.value = true;

        if (originalDate.value) {
            roundDateToNearestFiveMinutes(originalDate.value);
        }

        await loadCurrentMonthCalendar();
    }
};

const undoUpdateDate = () => {
    date.value = originalDate.value;

    if (originalDate.value) {
        updateDate.value = false;
    }
};
</script>
